import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Button,
  Snackbar,
  Alert,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import moment from 'moment';
import MobileHeader from '../components/MobileHeader';

const VideoScreen = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    loadData(true);
  }, []);

  const loadData = async (isInitial = false) => {
    try {
      if (isInitial) {
        setLoading(true);
        setPageNumber(1);
        setHasMoreData(true);
      }
      const currentPage = isInitial ? 1 : pageNumber;
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        `/api/school-common/video/get-video-student?PageSize=10&PageNumber=${currentPage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const newData = response.data.data || [];
      if (isInitial) {
        setItems(newData);
      } else {
        setItems((prev) => [...prev, ...newData]);
      }
      if (newData.length === 10) {
        setPageNumber((prev) => prev + 1);
      } else {
        setHasMoreData(false);
      }
    } catch (error) {
      console.error('Error loading videos:', error);
      handleCatch(error);
      if (isInitial) setItems([]);
      setNotification({ open: true, message: 'Failed to load videos', severity: 'error' });
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const onRefresh = async () => {
    await loadData(true);
  };

  const loadMoreData = async () => {
    if (hasMoreData && !loading) {
      setLoading(true);
      await loadData(false);
    }
  };

  const handlePlayVideo = (videoUrl) => {
    try {
      const fullUrl = `https://vimeo.com/${videoUrl}`;
      window.open(fullUrl, '_blank');
    } catch (error) {
      console.error('Error opening video:', error);
      setNotification({ open: true, message: 'Failed to open video', severity: 'error' });
    }
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#F5F7FA' }}>
      <MobileHeader
        title="Videos"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pt: 1, pb: 2 }}>
        {initialLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 10 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: 14 }}>Loading videos...</Typography>
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 10, px: 3 }}>
            <Typography sx={{ fontSize: 64, mb: 1.5 }}></Typography>
            <Typography sx={{ fontWeight: 'bold', fontSize: 17, mb: 1, textAlign: 'center' }}>
              No Videos Available
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'center', mb: 3 }}>
              Educational videos will appear here. Check back later!
            </Typography>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={onRefresh}>
              Refresh
            </Button>
          </Box>
        ) : (
          <>
            {items.map((item, index) => (
              <Box
                key={index}
                sx={{
                  bgcolor: '#fff',
                  borderRadius: 2,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  mx: 0.5,
                  mt: 1,
                  overflow: 'hidden',
                }}
              >
                {/* 16:9 Thumbnail */}
                <Box
                  onClick={() => handlePlayVideo(item.videoUrl)}
                  sx={{
                    position: 'relative',
                    paddingTop: '56.25%', // 16:9
                    bgcolor: '#1a1a2e',
                    cursor: 'pointer',
                  }}
                >
                  {/* Play button circle */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.9)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PlayArrowIcon sx={{ fontSize: 34, color: colors.BLUE, ml: 0.5 }} />
                  </Box>
                  {/* Category label */}
                  {item.category && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: 8,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                        fontSize: 10,
                        px: 1,
                        py: 0.3,
                        borderRadius: 1,
                      }}
                    >
                      {item.category}
                    </Box>
                  )}
                </Box>

                {/* Text content */}
                <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 'bold',
                      fontSize: 14,
                      color: '#222',
                      mb: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {item.title}
                  </Typography>
                  {item.description && (
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: '#666',
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {item.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {item.createdBy && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <PersonIcon sx={{ fontSize: 12, color: '#888' }} />
                        <Typography sx={{ fontSize: 11, color: '#888' }}>{item.createdBy}</Typography>
                      </Box>
                    )}
                    {item.creationDate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <CalendarTodayIcon sx={{ fontSize: 12, color: '#888' }} />
                        <Typography sx={{ fontSize: 11, color: '#888' }}>
                          {moment(item.creationDate).format('MMM DD, YYYY')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                <Divider />

                <Box sx={{ px: 2, py: 1.2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<PlayArrowIcon />}
                    onClick={() => handlePlayVideo(item.videoUrl)}
                    sx={{
                      bgcolor: colors.BLUE,
                      height: 42,
                      borderRadius: 21,
                      fontWeight: 'bold',
                      textTransform: 'none',
                      fontSize: 13,
                      '&:hover': { bgcolor: '#0052A3' },
                    }}
                  >
                    Play Video
                  </Button>
                </Box>
              </Box>
            ))}

            {hasMoreData && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
                <Button
                  variant="outlined"
                  onClick={loadMoreData}
                  disabled={loading}
                  sx={{ borderRadius: 20, px: 4 }}
                >
                  {loading ? <CircularProgress size={22} /> : 'Load More Videos'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VideoScreen;
